import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useApi } from '../services/api';
import Editor from '@monaco-editor/react';

interface Template {
  id: string;
  name: string;
  vendor: string;
  switch_type: string;
  description?: string;
  is_primary: boolean;
  is_standard: boolean;
  supported_features?: Record<string, boolean>;
  parameter_schema?: any;
  template_content?: string;
}

interface TemplateSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: Template) => void;
  vendor: string;
  switchType: string;
  title?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  open,
  onClose,
  onSelect,
  vendor,
  switchType,
  title = "Select Configuration Template"
}) => {
  const { getAuthenticatedApi } = useApi();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open, vendor, switchType]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await getAuthenticatedApi();
      const response = await api.getTemplatesByType(vendor, switchType);
      
      const templatesArray = Array.isArray(response) ? response : [];
      setTemplates(templatesArray);
      
      // Auto-select primary template if available
      const primaryTemplate = templatesArray.find(t => t.is_primary);
      if (primaryTemplate) {
        setSelectedTemplateId(primaryTemplate.id);
      } else if (templatesArray.length > 0) {
        setSelectedTemplateId(templatesArray[0].id);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (template: Template) => {
    if (!template.template_content) {
      // Load full template details
      try {
        const api = await getAuthenticatedApi();
        const fullTemplate = await api.getTemplate(template.id);
        setPreviewTemplate(fullTemplate);
      } catch (error) {
        console.error('Failed to load template details:', error);
        setPreviewTemplate(template);
      }
    } else {
      setPreviewTemplate(template);
    }
    setPreviewOpen(true);
  };

  const handleSelect = () => {
    const selected = templates.find(t => t.id === selectedTemplateId);
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  const getFeatureChips = (features?: Record<string, boolean>) => {
    if (!features) return null;
    
    const enabledFeatures = Object.entries(features)
      .filter(([_, enabled]) => enabled)
      .map(([feature]) => feature);
    
    if (enabledFeatures.length === 0) return null;
    
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
        {enabledFeatures.slice(0, 3).map(feature => (
          <Chip
            key={feature}
            label={feature}
            size="small"
            variant="outlined"
            color="primary"
          />
        ))}
        {enabledFeatures.length > 3 && (
          <Chip
            label={`+${enabledFeatures.length - 3} more`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <CategoryIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          {loading ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Loading templates...
              </Typography>
              <LinearProgress />
            </Box>
          ) : templates.length === 0 ? (
            <Alert severity="info">
              No templates available for {vendor} {switchType} switches.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a template for your {vendor} {switchType} switch configuration.
                {templates.some(t => t.is_primary) && " The recommended template is marked with a star."}
              </Typography>
              
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  <Grid container spacing={2}>
                    {templates.map((template) => (
                      <Grid size={12} key={template.id}>
                        <Card 
                          variant={selectedTemplateId === template.id ? "elevation" : "outlined"}
                          sx={{ 
                            position: 'relative',
                            border: selectedTemplateId === template.id ? '2px solid' : '1px solid',
                            borderColor: selectedTemplateId === template.id ? 'primary.main' : 'divider'
                          }}
                        >
                          <CardContent>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                              <FormControlLabel
                                value={template.id}
                                control={<Radio />}
                                label=""
                                sx={{ mt: -1 }}
                              />
                              <Box flex={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {template.name}
                                  </Typography>
                                  {template.is_primary && (
                                    <Tooltip title="Recommended template">
                                      <StarIcon color="primary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                  {template.is_standard && (
                                    <Chip 
                                      label="Standard" 
                                      size="small" 
                                      color="info" 
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                                
                                {template.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {template.description}
                                  </Typography>
                                )}
                                
                                {getFeatureChips(template.supported_features)}
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(template)}
                                title="Preview template"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSelect}
            disabled={!selectedTemplateId || loading}
            startIcon={<CheckCircleIcon />}
          >
            Use This Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Template Preview: {previewTemplate?.name}</Typography>
            {previewTemplate?.is_primary && (
              <Chip 
                icon={<StarIcon />}
                label="Primary Template" 
                color="primary" 
                variant="outlined"
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          {previewTemplate?.description && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {previewTemplate.description}
            </Alert>
          )}
          
          {previewTemplate?.supported_features && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <BuildIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Supported Features:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(previewTemplate.supported_features)
                  .filter(([_, enabled]) => enabled)
                  .map(([feature]) => (
                    <Chip
                      key={feature}
                      label={feature.replace(/_/g, ' ').toUpperCase()}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
              </Stack>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            Template Content:
          </Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Editor
              height="400px"
              defaultLanguage="django"
              value={previewTemplate?.template_content || ''}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 12
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateSelector;